import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Smartphone, 
  Globe, 
  Workflow, 
  Brain,
  ArrowRight,
  ExternalLink,
  Loader2
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioPage = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { value: "all", label: "Tümü" },
    { value: "mobile_app", label: "Mobil Uygulama", icon: Smartphone },
    { value: "web_app", label: "Web Uygulama", icon: Globe },
    { value: "automation", label: "Otomasyon", icon: Workflow },
    { value: "ai_solution", label: "Yapay Zeka", icon: Brain }
  ];

  const getCategoryIcon = (category) => {
    const icons = {
      mobile_app: Smartphone,
      web_app: Globe,
      automation: Workflow,
      ai_solution: Brain
    };
    return icons[category] || Globe;
  };

  const getCategoryColor = (category) => {
    const colors = {
      mobile_app: "#00d4aa",
      web_app: "#0ea5e9",
      automation: "#a855f7",
      ai_solution: "#f97316"
    };
    return colors[category] || "#00d4aa";
  };

  const getCategoryLabel = (category) => {
    const labels = {
      mobile_app: "Mobil Uygulama",
      web_app: "Web Uygulama",
      automation: "Otomasyon",
      ai_solution: "Yapay Zeka"
    };
    return labels[category] || category;
  };

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolio?active_only=true`);
      setPortfolio(response.data);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPortfolio = selectedCategory === "all" 
    ? portfolio 
    : portfolio.filter(p => p.category === selectedCategory);

  return (
    <div className="pt-16 md:pt-20" data-testid="portfolio-page">
      {/* Hero */}
      <section className="py-20 bg-[#0a0a0f] relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#00d4aa] font-mono text-sm mb-4 block">
              {'// PORTFÖY'}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#f0f0f5] mb-6">
              Tamamladığımız{" "}
              <span className="gradient-text">Projeler</span>
            </h1>
            <p className="text-lg text-[#9ca3af] leading-relaxed">
              Farklı sektörlerden müşterilerimiz için geliştirdiğimiz 
              başarılı projeleri inceleyin.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#12121a] border-b border-[#2a2a3a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === cat.value
                    ? 'bg-[#00d4aa] text-[#0a0a0f]'
                    : 'bg-[#16161f] text-[#9ca3af] border border-[#2a2a3a] hover:border-[#00d4aa] hover:text-[#00d4aa]'
                }`}
                data-testid={`portfolio-filter-${cat.value}`}
              >
                {cat.icon && <cat.icon className="w-4 h-4" />}
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 bg-[#12121a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
            </div>
          ) : filteredPortfolio.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-[#16161f] border border-[#2a2a3a] flex items-center justify-center mx-auto mb-4">
                <Globe className="w-10 h-10 text-[#9ca3af]" />
              </div>
              <h3 className="text-xl font-semibold text-[#f0f0f5] mb-2">
                Henüz Proje Eklenmemiş
              </h3>
              <p className="text-[#9ca3af] mb-6">
                Bu kategoride henüz proje bulunmuyor.
              </p>
              <Link to="/iletisim" className="btn-primary inline-flex items-center gap-2">
                Proje Talebi
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPortfolio.map((project) => {
                const IconComponent = getCategoryIcon(project.category);
                const categoryColor = getCategoryColor(project.category);
                
                return (
                  <Link
                    key={project.id}
                    to={`/portfolyo/${project.id}`}
                    className={`bg-[#16161f] border rounded-xl overflow-hidden transition-all hover:border-[#00d4aa] hover:shadow-lg hover:shadow-[#00d4aa]/10 group cursor-pointer block ${
                      project.is_featured ? 'border-[#00d4aa]' : 'border-[#2a2a3a]'
                    }`}
                    data-testid={`portfolio-card-${project.id}`}
                  >
                    {/* Image placeholder or actual image */}
                    <div className="h-48 bg-gradient-to-br from-[#0a0a0f] to-[#16161f] relative overflow-hidden">
                      {project.image_url ? (
                        <img 
                          src={project.image_url} 
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <IconComponent 
                            className="w-16 h-16 opacity-20" 
                            style={{ color: categoryColor }}
                          />
                        </div>
                      )}
                      
                      {project.is_featured && (
                        <div className="absolute top-4 left-4 px-3 py-1 bg-[#00d4aa] text-[#0a0a0f] text-xs font-semibold rounded-full">
                          Öne Çıkan
                        </div>
                      )}
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span 
                          className="text-xs font-medium px-3 py-1 rounded-full"
                          style={{ 
                            backgroundColor: `${categoryColor}15`,
                            color: categoryColor
                          }}
                        >
                          {getCategoryLabel(project.category)}
                        </span>
                        {project.client_name && (
                          <span className="text-xs text-[#9ca3af]">
                            {project.client_name}
                          </span>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-[#f0f0f5] mb-2 group-hover:text-[#00d4aa] transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-[#9ca3af] text-sm mb-4 line-clamp-2">
                        {project.description}
                      </p>

                      {project.technologies && project.technologies.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {project.technologies.slice(0, 4).map((tech, idx) => (
                            <span 
                              key={idx}
                              className="text-xs px-2 py-1 bg-[#0a0a0f] text-[#9ca3af] rounded"
                            >
                              {tech}
                            </span>
                          ))}
                          {project.technologies.length > 4 && (
                            <span className="text-xs px-2 py-1 text-[#9ca3af]">
                              +{project.technologies.length - 4}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-2 text-sm text-[#00d4aa] group-hover:text-[#0ea5e9] transition-colors">
                          Detayları Gör
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                        
                        {project.project_url && (
                          <span
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(project.project_url, '_blank');
                            }}
                            className="inline-flex items-center gap-1 text-xs text-[#9ca3af] hover:text-[#00d4aa] transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0a0a0f] relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-[#00d4aa] font-mono text-sm mb-4 block">
            {'// SİZİN PROJENİZ'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#f0f0f5] mb-6">
            Bir Sonraki Proje{" "}
            <span className="gradient-text">Sizinki Olsun</span>
          </h2>
          <p className="text-[#9ca3af] mb-8 max-w-2xl mx-auto">
            Fikirlerinizi hayata geçirmek için buradayız. 
            Projenizi portföyümüze eklemeye hazırız.
          </p>
          <Link
            to="/iletisim"
            className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            data-testid="portfolio-cta"
          >
            Proje Başlat
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;
